import React from 'react'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import PrizeBadge from '../../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useNavigate } from 'react-router-dom'
import { Col, Container, Row } from 'react-bootstrap'
import { SelectDropdown } from '../../../../components/SelectDropdown/SelectDropdown'
import { EmailAction } from '../../../../models/Account'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { useGetAllEmailTypesQuery } from '../../../../store/integrations/emailTypes'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import './EmailTypeSettings.scss'
import { EmailTypeKind } from '../../../../models/EmailType'

const EMAIL_ACTION_OPTIONS = [
  { id: 'doNotReply', label: 'Do not reply' },
  { id: 'generateReply', label: 'Generate reply' },
  { id: 'pushToDraft', label: 'Push to draft' },
]

export const EmailTypeSettings = () => {
  const navigate = useNavigate()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const { hasQuota } = useZocksQuota()
  const emailEnabled = hasQuota('integration.emailEnabled.value')

  const { data: account, isLoading: accountLoading } = useGetAccountQuery({ accountId })
  const { data: emailTypes, isLoading: emailTypesLoading } = useGetAllEmailTypesQuery({ accountId, kind: EmailTypeKind.REPLY })

  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()

  if (accountLoading || !account?.data || emailTypesLoading || !emailTypes?.data) {
    return <div className='skeleton' style={{ width: '100%', height: '60px' }} />
  }

  const _handleEmailActionSelected = (emailTypeId: string, action: EmailAction) => {
    if (!account) {
      return
    }
    const { config } = account.data
    const { email } = config
    const { emailActionsByEmailType } = email
    const accountConfig = {
      ...config,
      email: {
        ...email,
        emailActionsByEmailType: {
          ...emailActionsByEmailType,
          [emailTypeId]: action,
        },
      },
    }
    updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const handleEmailActionSelected = tracked(
    _handleEmailActionSelected,
    { eventName: TrackEvent.EMAIL_AUTOMATION_SETTINGS_CHANGED },
    {
      eventDetailsExtractor: (emailTypeId: string, action: EmailAction) => {
        return {
          emailTypeId,
          action,
        }
      },
    },
  )

  const handleUncategorizedEmailActionSelected = (action: EmailAction) => {
    if (!account) {
      return
    }
    const { config } = account.data
    const { email } = config
    const accountConfig = { ...config, email: { ...email, uncategorizedEmailAction: action } }

    updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  return (
    <div className='d-flex flex-column zocks-gap-2.5'>
      <h4>Email type based automation</h4>
      <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
        <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
        <span className='paragraph-medium zocks-text-gray-900'>
          The AI Assistant identifies the main topic in your client mails and applies an Email Type to categorize it. You can use Email Types to set up
          automations or apply different reply templates.
        </span>
        {!emailEnabled && (
          <ScopedBlock
            scope={{
              scope: 'account:update',
              superScope: ['account:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Button
              className='d-flex p-1 p-2 align-items-center text-nowrap'
              variant='primary'
              onClick={() => {
                navigate('/account/plan')
              }}
            >
              <img alt='Upgrade' src={PrizeBadge} />
              Get feature
            </Button>
          </ScopedBlock>
        )}
      </div>
      <div className='d-flex flex-column zocks-gap-2'>
        <div className='paragraph-medium'>
          New Email Types are auto populated. You can use the Actions dropdown to define if a Reply is automatically prepared in Zocks or if you would like to
          push it to your Draft folder in your Email tool.{' '}
        </div>
        <Container className='p-0'>
          <Row className='p-3 d-none d-lg-flex'>
            <Col>
              <div className='lead-small'>Email type</div>
            </Col>
            <Col>
              <div className='lead-small'>Action</div>
            </Col>
          </Row>
          {emailTypes.data.map((emailType) => (
            <React.Fragment key={emailType.id}>
              <div className='d-none d-lg-flex w-100 email-type-row'></div>
              <Row className='p-3 g-3 g-lg-0'>
                <Col xs={12} lg className='d-flex align-items-center gap-3 text-truncate'>
                  <i className='bi bi-envelope zocks-text-primary zocks-bg-color-primary-light zocks-border-0.25 email-type-row-icon d-flex align-items-center justify-content-center fs-5' />
                  <div className='paragraph-medium bold text-truncate'>{emailType.name}</div>
                </Col>
                <Col xs={12} lg>
                  <ScopedBlock
                    scope={{
                      scope: 'account:update',
                      superScope: ['account:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <SelectDropdown
                      options={EMAIL_ACTION_OPTIONS}
                      initialSelectedOption={EMAIL_ACTION_OPTIONS.find((option) => {
                        const actionForEmailType = account.data.config.email.emailActionsByEmailType[emailType.id] ?? 'doNotReply'
                        return option.id === actionForEmailType
                      })}
                      onOptionSelected={(action) => handleEmailActionSelected(emailType.id, action.id as EmailAction)}
                      disabled={accountUpdateLoading || !emailEnabled}
                    />
                  </ScopedBlock>
                </Col>
              </Row>
            </React.Fragment>
          ))}
          <Row className='p-3 g-3 g-lg-0 mt-4 zocks-bg-color-gray-100 zocks-border-0.5'>
            <Col xs={12} lg className='d-flex align-items-center gap-3'>
              <i className='bi bi-envelope zocks-bg-color-gray-200 zocks-border-0.25 email-type-row-icon d-flex align-items-center justify-content-center fs-5' />
              <div className='d-flex gap-2'>
                <div className='paragraph-medium bold'>Uncategorized</div>
                <Tooltip title='Emails that don’t match any Email Type description' placement='top'>
                  <i className='bi bi-info-square fs-6' />
                </Tooltip>
              </div>
            </Col>
            <Col xs={12} lg>
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <SelectDropdown
                  options={EMAIL_ACTION_OPTIONS}
                  initialSelectedOption={EMAIL_ACTION_OPTIONS.find((option) => {
                    return option.id === account.data.config.email.uncategorizedEmailAction
                  })}
                  onOptionSelected={(action) => handleUncategorizedEmailActionSelected(action.id as EmailAction)}
                  disabled={accountUpdateLoading || !emailEnabled}
                />
              </ScopedBlock>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  )
}
